<template>
    <div
        class="fulfillment"
        :class="{
            'is-paid': model.isPaid,
            'is-unpaid': !model.isPaid,
            'is-fulfilled': model.isFulfilled,
        }"
        @click="handleClick"
    >
        <div class="head" v-if="productionTileViewMode === ProductionTileViewMode.Detailed">
            <div class="info">
                <div class="order">
                    <div class="order-number-with-icon">
                        <span
                            v-if="productionTileViewMode === ProductionTileViewMode.Detailed"
                            class="order-number pr-1"
                        >
                            {{ orderNumberToDisplay }}
                        </span>
                        <i v-if="model.isLinked" class="pr-1 pl-1 fa-light fa-link"></i>
                        <i v-else-if="model.orderHasOtherFulfillments" class="pr-1 fa-light fa-paperclip"></i>
                    </div>
                    <span class="groupName">{{ groupName }}</span>
                </div>
                <div class="customerTable">
                    <span class="tableName">{{ tableName }}</span>
                    <span class="useCaseName">{{ useCaseName }}</span>
                </div>
                <div class="ordertime">{{ orderTime }}</div>
            </div>
            <FulfillmentQueueIndicatorComponent
                :fulfillment="fulfillment"
                :currentTime="props.currentTime"
                :group="group"
            />
        </div>

        <div class="body" :class="{ 'has-no-header': productionTileViewMode === ProductionTileViewMode.Overview }">
            <template v-if="productionTileViewMode === ProductionTileViewMode.Detailed">
                <div class="itemslist">
                    <OrderItemsList :orderItems="model.items" :displayPrice="false" :isCompact="true" />
                </div>

                <div class="actions">
                    <!-- duration button -->
                    <div
                        v-if="!isPositionDisplayMode"
                        class="button increasetime"
                        :class="{ disabled: isProcessing || isWaitingForUpdate }"
                        @click.stop="handleIncreaseTime"
                    >
                        {{ increaseTimeButtonText }}
                    </div>

                    <!-- position button - not, maybe will be enabled in the future -->
                    <div
                        v-if="isPositionDisplayMode && isPositionChangeButtonShown"
                        class="button increasetime"
                        :class="{ disabled: isProcessing || isWaitingForUpdate }"
                        @click.stop="handleIncreasePosition"
                    >
                        {{ increasePositionButtonText }}
                    </div>

                    <!-- set ready button -->
                    <div
                        class="button resolve"
                        v-if="isInPreparation"
                        :class="{ disabled: isProcessing || isWaitingForUpdate }"
                        @click.stop="handleSetFulfillmentReady"
                    >
                        <i class="fal fa-cogs"></i><i class="fal fa-check"></i>
                    </div>

                    <!-- set fulfilled button -->
                    <div
                        class="button resolve"
                        v-if="!isInPreparation"
                        :class="{ disabled: isSetFulfillmentFulfilledDisabled || isWaitingForUpdate }"
                        @click.stop="handleSetFulfillmentFulfilled"
                    >
                        <i class="fa-light fa-person-carry-box"></i><i class="fal fa-check"></i>
                    </div>
                </div>
            </template>

            <template v-if="productionTileViewMode === ProductionTileViewMode.Overview">
                <div class="order-number-container" :class="{ 'is-ready-shortly': isReadyShortly, 'is-ready': isReady }">
                    <div v-if="model.dailyOrderIncrement !== undefined" class="big-order-daily-increment" >
                        {{ OrderHelper.padDailyIncrement(model.dailyOrderIncrement!) }}
                    </div>
                    <div v-else class="big-order-number">{{ model.orderNumber }}</div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import {
        FulfillmentPositionDisplayMode,
        FulfillmentProductionState,
        ProductionTileViewMode,
    } from "@/data/model/Enums";
    import type { IExtendedOperatorFulfillment } from "@/data/model/Operator";
    import { useOperatorSettingsStore } from "@/data/stores/operatorSettings";
    import { DateTime } from "@/data/util/DateTime";
    import { OrderHelper } from "@/data/util/OrderHelper";
    import { FulfillmentHelper } from "@/operator/data/FulfillmentHelper";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { trans } from "@/operator/localization/Trans";
    import { translateByLocationLanguage } from "@/shared/localization/language";
    import { logError } from "@/shared/logging/errors";
    import { computed, onBeforeUnmount, ref, watch } from "vue";

    import OrderItemsList from "@/operator/components/order/OrderItemsList.vue";
    import FulfillmentQueueIndicatorComponent from "@/operator/components/production/FulfillmentQueueIndicatorComponent.vue";

    const props = defineProps<{
        fulfillment: IExtendedOperatorFulfillment;
        currentTime: Date;
    }>();

    // State
    const operatorSettings = useOperatorSettingsStore();
    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);

    // Computed
    const productionTileViewMode = computed(
        () => operatorSettings.productionTileViewMode ?? ProductionTileViewMode.Detailed
    );

    const model = computed(() => props.fulfillment);
    const group = computed(() => props.fulfillment.fulfillmentGroup);
    const groupName = computed(() => FulfillmentHelper.getFulfillmentGroupName(group.value));
    const orderNumberToDisplay = computed(() =>
        model.value.dailyOrderIncrement !== undefined
            ? OrderHelper.padDailyIncrement(model.value.dailyOrderIncrement)
            : model.value.orderNumber
    );

    const tableName = computed(() =>
        model.value?.tableName == null ? trans.production.deletedTable : model.value?.tableName
    );
    const useCaseName = computed(() => translateByLocationLanguage(model.value.useCaseName));

    const orderTime = computed(() => {
        let orderTime = model.value.scheduledFulfillmentDateTime;
        if (model.value.effectiveFulfillmentDateTime) {
            orderTime = model.value.effectiveFulfillmentDateTime;
        } else if (model.value.effectiveReadyDateTime) {
            orderTime = model.value.effectiveReadyDateTime;
        }
        return DateTime.toTimeString(orderTime, true);
    });

    const isPositionDisplayMode = computed(() => {
        return model.value.fulfillmentPositionDisplayMode === FulfillmentPositionDisplayMode.Position;
    });

    const isInPreparation = computed(
        () =>
            model.value.productionState === FulfillmentProductionState.ReadyShortly ||
            model.value.productionState === FulfillmentProductionState.InPreparation
    );

    const isReadyShortly = computed<boolean>(
        () => model.value.productionState === FulfillmentProductionState.ReadyShortly
    );

    const isReady = computed(
        () =>
            model.value.productionState === FulfillmentProductionState.Ready
    );

    function handleClick() {
        operatorEvents.productionController.onFulfillmentClick.dispatch({
            fulfillment: model.value,
            fulfillmentGroup: model.value.fulfillmentGroup,
        });
    }

    // #region Action buttons
    const isSetFulfillmentFulfilledDisabled = computed(() => model.value.isFulfilled || isProcessing.value);
    const isProcessing = ref(false);

    // #region increase time
    const increaseTimeMinutes = computed(() => (isInPreparation.value ? 5 : 3)); // 3 minutes when ready, +5 when in preparation
    const isIncreaseTimeRelative = computed(() => isInPreparation.value);
    const increaseTimeButtonText = computed(
        () => (isIncreaseTimeRelative.value ? "+" : "") + increaseTimeMinutes.value + " m"
    );

    async function handleIncreaseTime() {
        if (isProcessing.value) return;

        try {
            isProcessing.value = true;
            startWaitingForUpdate();
            await operatorDao.updateFulfillmentTime(model.value.id, {
                isRelative: isIncreaseTimeRelative.value,
                orderId: model.value.orderId,
                preparationTime: DateTime.secondsToTimespan(increaseTimeMinutes.value * 60),
            });
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: trans.general.failed,
                message: trans.general.operationFailed,
                uniqueId: "operation-failed-popup",
            });
            clearIsWaitingForUpdate();
        } finally {
            isProcessing.value = false;
        }
    }
    // #endregion

    // #region increase position
    const isPositionChangeButtonShown = ref(false);
    const positionChangeNumber = computed(() => (isInPreparation.value ? 1 : 1)); // 1st position when ready, +1 position when in preparation
    const isIncreasePositionRelative = computed(() => isInPreparation.value);
    const increasePositionButtonText = computed(
        () => (isIncreasePositionRelative.value ? "+" : "") + positionChangeNumber.value + " m"
    );
    async function handleIncreasePosition() {
        if (isProcessing.value) return;

        try {
            isProcessing.value = true;
            startWaitingForUpdate();
            await operatorDao.updateFulfillmentPosition(model.value.id, {
                isRelative: isIncreasePositionRelative.value,
                positionChange: positionChangeNumber.value,
            });
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: trans.general.failed,
                message: trans.general.operationFailed,
                uniqueId: "operation-failed-popup",
            });
            clearIsWaitingForUpdate();
        } finally {
            isProcessing.value = false;
        }
    }
    // #endregion

    // ready button
    async function handleSetFulfillmentReady() {
        if (isProcessing.value) return;

        try {
            isProcessing.value = true;
            startWaitingForUpdate();
            await operatorDao.setFulfillmentReady(model.value.id, {
                orderId: model.value.orderId,
            });
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: trans.general.failed,
                message: trans.general.operationFailed,
                uniqueId: "operation-failed-popup",
            });
            clearIsWaitingForUpdate();
        } finally {
            isProcessing.value = false;
        }
    }

    // fulfilled button
    async function handleSetFulfillmentFulfilled() {
        if (isProcessing.value) return;

        try {
            isProcessing.value = true;
            startWaitingForUpdate();
            await operatorDao.setFulfillmentFulfilled(model.value.id, {
                orderId: model.value.orderId,
            });
        } catch (ex) {
            logError(ex);
            Popup.show({
                title: trans.general.failed,
                message: trans.general.operationFailed,
                uniqueId: "operation-failed-popup",
            });
            clearIsWaitingForUpdate();
        } finally {
            isProcessing.value = false;
        }
    }
    //#endregion

    // #region waiting for update
    const isWaitingForUpdate = ref(false);
    const waitingForUpdateTimeout = ref<number | null>(null);

    function startWaitingForUpdate() {
        const maxSecondsToWait = 20;
        isWaitingForUpdate.value = true;
        waitingForUpdateTimeout.value = window.setTimeout(() => {
            isWaitingForUpdate.value = false;
        }, maxSecondsToWait * 1000);
    }

    function clearIsWaitingForUpdate() {
        if (waitingForUpdateTimeout.value) {
            window.clearTimeout(waitingForUpdateTimeout.value);
            waitingForUpdateTimeout.value = null;
        }
        isWaitingForUpdate.value = false;
    }
    // #endregion

    watch(
        () => props.fulfillment,
        () => {
            clearIsWaitingForUpdate();
        },
        { deep: true }
    );

    onBeforeUnmount(clearIsWaitingForUpdate);
</script>
